// Chapter 24 - Extinction
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 24.

import type { ChapterModule } from "./types";

const chapter24Module: ChapterModule = {
  meta: {
    id: "24",
    number: 24,
    title: "Extinction",
    subtitle: "Extinction burst, spontaneous recovery, resistance to extinction",
    part: "Part 9 - Decreasing Behavior with Nonpunishment Procedures",
    estMinutes: 60,
    bacb: ["B-9", "G-9"],
  },
  curated: true,
  overview: [
    "Chapter 24 defines extinction three ways: as a PROCEDURE (zero probability of reinforcement), a PROCESS (the diminishing rate that follows), and a PRINCIPLE (the functional relation between withholding reinforcement and the resulting decline).",
    "You will learn the three procedural forms tied to the maintaining contingency (positive reinforcement, negative reinforcement, automatic reinforcement), the secondary effects (extinction burst, response variation, magnitude increase, spontaneous recovery, resurgence, emotional outbursts and aggression), variables affecting resistance to extinction (schedule type, EO strength, reinforcer magnitude, response effort), ten guidelines for using extinction effectively, and the situations where extinction should NOT be used alone (harmful behavior, uncontrollable reinforcers, urgency, modeling risk).",
  ],
  objectives: [
    { id: "B-9", text: "Define and provide examples of operant extinction." },
    { id: "G-9", text: "Use extinction procedures to decrease problem behavior, including positive, negative, and automatic reinforcement variants." },
  ],
  coreConcepts: [
    {
      title: "Three meanings of extinction",
      body: [
        "Extinction as a PROCEDURE: zero probability of reinforcement for a previously reinforced behavior. Extinction as a PROCESS: the diminishing rate that follows. Extinction as a PRINCIPLE: the functional relation between withholding reinforcement and the resulting decline.",
      ],
    },
    {
      title: "Procedure vs function (FBA matters)",
      body: [
        "FBA enables behavior analysts to distinguish PROCEDURAL extinction ('just ignore it') from FUNCTIONAL extinction (withholding the SPECIFIC reinforcer maintaining the behavior). Ignoring a behavior maintained by escape, automatic reinforcement, or specific items does NOT extinguish it.",
      ],
    },
    {
      title: "Three procedural forms by maintaining contingency",
      body: [
        "(1) POSITIVE REINFORCEMENT maintained behavior: withhold the reinforcer. (2) NEGATIVE REINFORCEMENT maintained behavior: ESCAPE EXTINCTION (do not allow the response to terminate or postpone the aversive event). (3) AUTOMATIC POSITIVE REINFORCEMENT: SENSORY EXTINCTION (mask or remove the sensory consequence). (4) AUTOMATIC NEGATIVE REINFORCEMENT: prevent the response from removing the aversive sensory stimulation.",
      ],
    },
    {
      title: "What extinction is NOT",
      body: [
        "Extinction is not any decrease in behavior; not forgetting; not response blocking; not noncontingent reinforcement.",
      ],
    },
    {
      title: "Secondary (side) effects of extinction",
      body: [
        "Common effects: (a) EXTINCTION BURST (temporary increase in frequency at the start), (b) RESPONSE VARIATION (new topographies), (c) INITIAL INCREASE IN RESPONSE MAGNITUDE, (d) SPONTANEOUS RECOVERY (responding reappears after a delay), (e) RESURGENCE (previously reinforced behaviors return), and (f) EMOTIONAL OUTBURSTS AND AGGRESSION.",
      ],
    },
    {
      title: "Variables affecting resistance to extinction",
      body: [
        "Resistance to extinction is relative. Variables include schedule type (intermittent generally produces more resistance than CRF; variable more than fixed), strength of the EO for the withheld reinforcer, the number/magnitude/quality of the reinforcer, repeated conditioning-extinction histories, and response effort.",
      ],
    },
    {
      title: "Ten guidelines for using extinction effectively",
      body: [
        "Withhold ALL reinforcers maintaining the behavior; withhold consistently; combine extinction with other procedures; use instructions when appropriate; plan for extinction-produced aggression; increase the number of extinction trials; include significant others; guard against unintentional extinction; maintain extinction-decreased behavior; track data continuously.",
      ],
    },
    {
      title: "When NOT to use extinction alone",
      body: [
        "Extinction should not be a singular intervention when: the target behavior is HARMFUL; all sources of reinforcement cannot be withheld; RAPID reduction is required; others are likely to IMITATE the problem behavior.",
      ],
    },
  ],
  glossary: [
    { term: "Extinction", definition: "Withholding reinforcement for a previously reinforced behavior, leading to a decreased response rate.", sourceHint: "Withholding reinforcement for a previously reinforced behavior decreases the rate of that behavior: a procedure, process, and principle known as extinction." },
    { term: "Procedural extinction", definition: "The procedure of withholding the reinforcer.", sourceHint: "As a behavior change procedure, extinction provides zero probability of reinforcement." },
    { term: "Functional extinction", definition: "Withholding the SPECIFIC reinforcer maintaining the behavior, identified via FBA.", sourceHint: "Functional behavior assessments enable behavior analysts to distinguish clearly between the procedural variations of extinction and the functional variations of extinction (i.e., identifying the specific reinforcer maintaining the behavior)." },
    { term: "Escape extinction", definition: "Extinction for negatively reinforced behavior: do not allow the response to terminate or postpone the aversive event.", sourceHint: "Behaviors maintained by negative reinforcement are placed on extinction (escape extinction) when those behaviors do not produce a removal of the aversive stimulus." },
    { term: "Sensory extinction", definition: "Extinction for automatic-positive-reinforcement behavior: mask or remove the sensory consequence.", sourceHint: "Behaviors maintained by automatic positive reinforcement are placed on extinction (sensory extinction) by masking or removing the sensory consequence." },
    { term: "Extinction burst", definition: "Temporary increase in response frequency at the onset of extinction.", sourceHint: "Common effects of extinction include (a) extinction burst." },
    { term: "Response variation", definition: "Emergence of new response topographies under extinction.", sourceHint: "Common effects of extinction include... (b) response variation." },
    { term: "Spontaneous recovery", definition: "Reappearance of an extinguished response after a passage of time without reinforcement.", sourceHint: "Common effects of extinction include... (d) spontaneous recovery." },
    { term: "Resurgence", definition: "Recurrence of previously reinforced responses when current responses are placed on extinction.", sourceHint: "Common effects of extinction include... (e) resurgence." },
    { term: "Resistance to extinction", definition: "The persistence of a behavior under extinction conditions; relative concept.", sourceHint: "Behavior that continues to occur during extinction is said to have greater resistance to extinction than behavior that diminishes quickly. Resistance to extinction is a relative concept." },
  ],
  procedures: [
    {
      title: "Implement functional extinction (positive reinforcement)",
      intro: "Identify the maintaining reinforcer and withhold it.",
      steps: [
        { title: "FBA confirms function", detail: "Use FBA (Ch 27) to identify the specific reinforcer maintaining the behavior." },
        { title: "Withhold ALL sources", detail: "Make sure every source of the maintaining reinforcer is withheld consistently." },
        { title: "Plan for the burst", detail: "Prepare staff/caregivers for the predictable temporary increase." },
        { title: "Reinforce alternatives", detail: "Combine with DRA/DRI/DRO (Ch 25) to provide an appropriate behavior that produces reinforcement." },
        { title: "Monitor and maintain", detail: "Track data, guard against unintentional extinction, plan for spontaneous recovery." },
      ],
    },
    {
      title: "Implement escape extinction",
      intro: "Hold the aversive antecedent in place when the problem behavior occurs.",
      steps: [
        { title: "Confirm escape function", detail: "FBA confirms negative reinforcement is maintaining the behavior." },
        { title: "Keep the demand", detail: "Do not allow the problem behavior to terminate or postpone the task/demand." },
        { title: "Teach replacement", detail: "Use functional communication training (e.g., 'break please') to provide appropriate escape." },
        { title: "Plan safety", detail: "Anticipate aggression and emotional outbursts; have safety protocols ready." },
      ],
    },
  ],
  examples: [
    { scenario: "A child who has previously received candy for whining no longer gets candy after whining; whining first spikes then declines.",
      application: "Extinction of behavior maintained by positive reinforcement; the initial spike is the extinction burst.",
      sourceHint: "Common effects of extinction include (a) extinction burst." },
    { scenario: "A learner who escaped math demands by screaming is no longer allowed to escape; screaming spikes briefly and then decreases.",
      application: "Escape extinction. Critical to maintain the demand throughout the burst.",
      sourceHint: "Behaviors maintained by negative reinforcement are placed on extinction (escape extinction) when those behaviors do not produce a removal of the aversive stimulus." },
    { scenario: "Two weeks after the behavior has been extinguished, the learner suddenly emits the problem behavior again before continuing not to.",
      application: "Spontaneous recovery; reinforce the alternative and avoid responding to the recovery.",
      sourceHint: "Common effects of extinction include... (d) spontaneous recovery." },
    { scenario: "A teacher uses sound-dampening earmuffs to neutralize the auditory feedback maintaining a repetitive vocal stereotypy.",
      application: "Sensory extinction for automatic positive reinforcement.",
      sourceHint: "Behaviors maintained by automatic positive reinforcement are placed on extinction (sensory extinction) by masking or removing the sensory consequence." },
  ],
  comparisons: [
    {
      title: "Procedural vs Functional extinction",
      left:  { label: "Procedural", points: [
        "Ignore the behavior - the colloquial recipe.",
        "Fails when reinforcer is not attention.",
      ]},
      right: { label: "Functional", points: [
        "Identify and withhold the specific maintaining reinforcer.",
        "Requires FBA.",
      ]},
    },
    {
      title: "Extinction burst vs Spontaneous recovery",
      left:  { label: "Extinction burst", points: [
        "Initial temporary INCREASE in response frequency.",
        "Occurs at the START of extinction.",
      ]},
      right: { label: "Spontaneous recovery", points: [
        "Reappearance AFTER the behavior has subsided.",
        "Often after a time interval without exposure.",
      ]},
    },
    {
      title: "Procedural extinction variants by function",
      left:  { label: "Positive / Automatic positive", points: [
        "Withhold the reinforcer (positive) or mask/remove sensory consequence (automatic positive).",
      ]},
      right: { label: "Negative / Automatic negative", points: [
        "Escape extinction: do not allow termination/postponement of aversive event.",
        "Automatic negative: prevent the response from removing aversive sensory stimulation.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Extinction means 'just ignore the behavior.'",
      reality: "Extinction means withholding the SPECIFIC maintaining reinforcer. If the behavior is maintained by escape, ignoring it actually reinforces it.",
      sourceHint: "Functional behavior assessments enable behavior analysts to distinguish clearly between the procedural variations of extinction (i.e., most commonly - 'Just ignore it, and it will go away.') and the functional variations of extinction (i.e., identifying the specific reinforcer maintaining the behavior).",
    },
    {
      myth: "An extinction burst means the procedure is failing.",
      reality: "An extinction burst is a predictable temporary effect at the start of extinction. The procedure is working; staff must stay consistent.",
      sourceHint: "Common effects of extinction include (a) extinction burst.",
    },
    {
      myth: "Once a behavior has been extinguished it will not return.",
      reality: "Spontaneous recovery and resurgence are common; plan to respond consistently when they appear.",
      sourceHint: "Common effects of extinction include... (d) spontaneous recovery... (e) resurgence.",
    },
    {
      myth: "Extinction is always appropriate as a first-line intervention.",
      reality: "Do NOT use extinction alone when behavior is harmful, when reinforcers cannot be controlled, when rapid reduction is required, or when imitation by others is likely.",
      sourceHint: "Extinction should not be used as a singular intervention in the following circumstances: when the target behavior is harmful, when all sources of reinforcement cannot be withheld, when a rapid reduction in behavior is required, or when others are likely to imitate the problem behavior.",
    },
    {
      myth: "Intermittent reinforcement makes extinction faster.",
      reality: "Intermittent reinforcement increases RESISTANCE to extinction; behavior maintained on variable schedules is typically more persistent under extinction.",
      sourceHint: "Intermittent schedules of reinforcement may produce behavior with greater resistance to extinction than the resistance produced by a continuous reinforcement schedule.",
    },
  ],
  examChecklist: [
    "Define extinction as a procedure, process, and principle.",
    "Distinguish procedural and functional extinction.",
    "Describe the three procedural forms tied to maintaining contingency.",
    "List six secondary effects of extinction.",
    "List five variables that affect resistance to extinction.",
    "List four situations where extinction should not be used alone.",
    "List five of the ten guidelines for using extinction effectively.",
    "Distinguish extinction from forgetting, response blocking, and NCR.",
  ],
  flashcardTerms: [
    "Extinction", "Procedural extinction", "Functional extinction",
    "Escape extinction", "Sensory extinction",
    "Extinction burst", "Response variation", "Spontaneous recovery", "Resurgence",
    "Resistance to extinction",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which is NOT an example of extinction?",
      options: [
        { label: "A", text: "Withholding the reinforcer for a previously reinforced behavior" },
        { label: "B", text: "Sensory extinction for automatic positive reinforcement" },
        { label: "C", text: "Escape extinction for negatively reinforced behavior" },
        { label: "D", text: "Forgetting" },
      ],
      answer: "D",
      explanation: "Extinction is the withholding of reinforcement for a previously reinforced behavior; it is not forgetting, response blocking, or NCR.",
      topic: "Definition",
      sourceHint: "Extinction (a) does not refer to any decrease in behavior, (b) is not the same as forgetting, (c) is not response blocking, and (d) is not noncontingent reinforcement.",
    },
    {
      id: "Q2",
      question: "A child screams to escape demands. The teacher refuses to remove the demand when screaming occurs. This is:",
      options: [
        { label: "A", text: "Sensory extinction" },
        { label: "B", text: "Escape extinction" },
        { label: "C", text: "Positive reinforcement extinction" },
        { label: "D", text: "Resurgence" },
      ],
      answer: "B",
      explanation: "Escape extinction holds the aversive antecedent in place; the response no longer terminates the demand.",
      topic: "Escape extinction",
      sourceHint: "Behaviors maintained by negative reinforcement are placed on extinction (escape extinction) when those behaviors do not produce a removal of the aversive stimulus.",
    },
    {
      id: "Q3",
      question: "An initial temporary INCREASE in response frequency at the start of extinction is called:",
      options: [
        { label: "A", text: "Resurgence" },
        { label: "B", text: "Spontaneous recovery" },
        { label: "C", text: "Extinction burst" },
        { label: "D", text: "Response variation" },
      ],
      answer: "C",
      explanation: "Extinction burst is the predictable initial frequency increase.",
      topic: "Side effects",
      sourceHint: "Common effects of extinction include (a) extinction burst.",
    },
    {
      id: "Q4",
      question: "Behaviors reinforced on which schedule typically show the GREATEST resistance to extinction?",
      options: [
        { label: "A", text: "Continuous reinforcement (CRF)" },
        { label: "B", text: "Fixed ratio (FR)" },
        { label: "C", text: "Variable ratio (VR), especially thin schedules" },
        { label: "D", text: "Noncontingent reinforcement" },
      ],
      answer: "C",
      explanation: "Variable schedules, especially thin variable schedules, produce the most persistent responding under extinction.",
      topic: "Resistance to extinction",
      sourceHint: "Variable schedules of reinforcement (e.g., VR, VI) may have more resistance to extinction than fixed schedules. To a degree, the thinner the intermittent schedule of reinforcement is, the greater the resistance to extinction will be.",
    },
    {
      id: "Q5",
      question: "Which is a contraindication for using extinction alone?",
      options: [
        { label: "A", text: "When the target behavior is harmful" },
        { label: "B", text: "When the function is well-understood" },
        { label: "C", text: "When the schedule is dense" },
        { label: "D", text: "When the learner has good replacement behaviors" },
      ],
      answer: "A",
      explanation: "Extinction should NOT be used as a singular intervention when behavior is harmful, when reinforcers cannot be controlled, when rapid reduction is needed, or when others are likely to imitate.",
      topic: "When not to use",
      sourceHint: "Extinction should not be used as a singular intervention in the following circumstances: when the target behavior is harmful, when all sources of reinforcement cannot be withheld, when a rapid reduction in behavior is required, or when others are likely to imitate the problem behavior.",
    },
  ],
  reviewPrompts: [
    "Plan a functional extinction program for an attention-maintained behavior including DRA support.",
    "Describe how you would prepare staff for an extinction burst.",
    "Distinguish sensory extinction from procedural extinction with one example.",
    "List four contraindications for extinction and explain each.",
    "Explain why variable schedules increase resistance to extinction.",
  ],
  recap: [
    "Extinction is the withholding of reinforcement for a previously reinforced behavior. It has three meanings (procedure, process, principle). FBA distinguishes procedural from functional extinction.",
    "Procedure differs by function: standard extinction for positive reinforcement, escape extinction for negative reinforcement, sensory extinction for automatic positive reinforcement. Side effects include burst, variation, magnitude increase, spontaneous recovery, resurgence, and emotional outbursts.",
    "Resistance to extinction rises with intermittent (especially variable, thin) schedules, strong EOs, large reinforcer magnitudes, repeated conditioning-extinction histories, and low response effort. Combine with DRA/DRI/DRO; don't use alone when behavior is harmful, reinforcers cannot be controlled, rapid reduction is required, or imitation is likely.",
  ],
  sourceExcerpts: [
    {
      heading: "Definition",
      text: "Withholding reinforcement for a previously reinforced behavior decreases the rate of that behavior: a procedure, process, and principle known as extinction.",
    },
    {
      heading: "Procedural forms by function",
      text: "Behaviors maintained by positive reinforcement are placed on extinction when those behaviors do not produce the reinforcer. Behaviors maintained by negative reinforcement are placed on extinction (escape extinction) when those behaviors do not produce a removal of the aversive stimulus. Behaviors maintained by automatic positive reinforcement are placed on extinction (sensory extinction) by masking or removing the sensory consequence.",
    },
    {
      heading: "Side effects",
      text: "Common effects of extinction include (a) extinction burst, (b) response variation, (c) initial increase in response magnitude, (d) spontaneous recovery, (e) resurgence, and (f) emotional outbursts and aggression.",
    },
    {
      heading: "When not to use",
      text: "Extinction should not be used as a singular intervention in the following circumstances: when the target behavior is harmful, when all sources of reinforcement cannot be withheld, when a rapid reduction in behavior is required, or when others are likely to imitate the problem behavior.",
    },
  ],
};

export default chapter24Module;
